import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { switchTenant, fetchTenantStatus } from '../store/slices/authSlice';
import { redirectToSalesforceLogin } from '../api/auth';
import { generateTraceId, logUserAction } from '../utils/trace';
import ProfileCard from '../components/auth/ProfileCard';

function TenantPickerPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tenants, activeTenant, tenantStatus, tenantStatusLoading } = useSelector((state) => state.auth);

  useEffect(() => {
    // Fetch tenant status for SF org info
    dispatch(fetchTenantStatus());
  }, [dispatch]);

  useEffect(() => {
    // If user already has an active tenant, redirect to dashboard
    if (activeTenant) {
      navigate('/dashboard');
    }
  }, [activeTenant, navigate]);

  const handleSelect = async (tenantId) => {
    const traceId = generateTraceId();
    logUserAction(traceId, 'select_tenant', { tenantId });
    await dispatch(switchTenant({ tenantId, traceId }));
    navigate('/dashboard');
  };

  const handleConnectSalesforce = () => {
    const traceId = generateTraceId();
    logUserAction(traceId, 'connect_salesforce', {});
    redirectToSalesforceLogin();
  };

  // Determine if user has Salesforce connected
  const hasSalesforce = tenantStatus?.salesforce_orgs && tenantStatus.salesforce_orgs.length > 0;

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="max-w-2xl mx-auto px-4 space-y-8">
        {/* Profile Section */}
        <ProfileCard />

        {/* Tenant Selection / Status Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {tenants.length > 0 ? 'Select a Workspace' : 'Workspace Access'}
          </h3>

          {/* Case: User has tenants to choose from */}
          {tenants.length > 0 && (
            <>
              <p className="text-gray-600 mb-6">
                You have access to multiple workspaces. Select one to continue.
              </p>
              <div className="space-y-3">
                {tenants.map((tenant) => (
                  <div
                    key={tenant.tenant_id}
                    onClick={() => handleSelect(tenant.tenant_id)}
                    className="p-4 rounded-lg border-2 border-gray-200 hover:border-primary-500 cursor-pointer transition"
                  >
                    <div className="font-semibold text-gray-900">{tenant.name}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Role: <span className="font-medium capitalize">{tenant.role}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Case: User has no tenants */}
          {tenants.length === 0 && (
            <div className="space-y-6">
              {tenantStatusLoading ? (
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ) : (
                <>
                  {/* If user has SF orgs, show their status */}
                  {hasSalesforce && (
                    <div className="space-y-4">
                      {tenantStatus.salesforce_orgs.map((org) => (
                        <div key={org.org_id} className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                          <div className="font-medium text-gray-900">{org.org_name}</div>

                          {org.tenant_exists ? (
                            // Tenant exists for this org
                            <div className="mt-2 text-sm text-gray-600">
                              {org.tenant_admin_email ? (
                                <>
                                  This organization has a workspace managed by{' '}
                                  <span className="font-medium">{org.tenant_admin_email}</span>.
                                  <br />
                                  Contact them to be added to the workspace.
                                </>
                              ) : (
                                <>
                                  This organization has a workspace, but no admin is configured.
                                  <br />
                                  Contact your Salesforce administrator.
                                </>
                              )}
                            </div>
                          ) : (
                            // No tenant for this org yet
                            <div className="mt-2 text-sm text-gray-600">
                              {org.is_admin ? (
                                <>
                                  You are a Salesforce admin for this organization.
                                  <br />
                                  Log in again with Salesforce to set up the workspace.
                                </>
                              ) : (
                                <>
                                  This organization is new to us.
                                  <br />
                                  Your Salesforce administrator needs to log in first to set up the workspace.
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* If user has no SF orgs, prompt to connect */}
                  {!hasSalesforce && (
                    <div className="text-center py-8">
                      <div className="text-gray-500 mb-4">
                        You don't have access to any workspaces yet.
                      </div>
                      <p className="text-sm text-gray-600 mb-6">
                        Connect your Salesforce account to join your organization's workspace.
                      </p>
                      <button
                        onClick={handleConnectSalesforce}
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-lg transition"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12.144 2C9.873 2 7.772 2.91 6.255 4.427c-1.518 1.517-2.427 3.618-2.427 5.889 0 .78.108 1.535.312 2.255C2.91 13.1 2 14.598 2 16.263c0 2.35 1.904 4.255 4.255 4.255.657 0 1.28-.15 1.836-.416.78 1.028 2.003 1.696 3.382 1.696 1.379 0 2.602-.668 3.382-1.696.555.267 1.179.416 1.836.416 2.35 0 4.255-1.904 4.255-4.255 0-1.665-.91-3.163-2.14-3.692.204-.72.312-1.475.312-2.255 0-4.6-3.718-8.316-8.316-8.316h-.658z"/>
                        </svg>
                        Connect Salesforce
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TenantPickerPage;
